import React, { useEffect, useMemo, useState } from "react";
import { PhoneIncoming } from "lucide-react";
import { getLatestInboundCallDate, safeDate } from "../../inboundCallHelpers";
import { useContextStateManagement } from "../../context/ContextApi";
import { DEFAULT_PRACTICE_TIMEZONE } from "../../practiceTimezone";
import InfoTooltip from "../../components/common/InfoTooltip";
import "./InboundOverview.css";

// Past 2h / Past 6h / Today - simple live ranges instead of the old 2h/6h/12h/24h picker,
// which didn't map to anything a user would actually ask for ("show me today" vs an arbitrary
// 12-hour window). "Today" has no fixed hour count - it always runs from the practice's own
// local midnight through now, however much of the day has elapsed (see isToday below).
const TIME_RANGES = [
  { key: "2h", label: "Past 2h", hours: 2 },
  { key: "6h", label: "Past 6h", hours: 6 },
  { key: "today", label: "Today", hours: null, isToday: true },
];

// Bucket resolution for the rolling-window graph - fine enough that a call plots close to its
// real time of day instead of snapping to one of only a handful of giant buckets (the original
// bug this whole rework fixes). Gridlines/labels are computed separately (see buildHourTicks
// below) at real clock-hour marks, decoupled from this count.
const ROLLING_WINDOW_BUCKETS = 48;

// Best-effort timezone for the one path with no server data at all to read from (the API-failed
// fallback branch below) - everywhere else uses apiVolume.practiceTimezone instead (see the
// overview useMemo), which is the user's own PreferredTimezone, resolved fresh per request. That
// value already IS the viewer's computer clock by default (App.js silently keeps it synced to
// the browser's detected zone on every login) but also correctly tracks the Settings page's
// "Fixed" timezone override when the user has deliberately set one - reading the raw browser
// clock directly here instead would silently ignore that deliberate override.
const FALLBACK_TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_PRACTICE_TIMEZONE;

const GRAPH_MIN = 0;
const GRAPH_TOP = 2;
const GRAPH_BOTTOM = 30;
const GRAPH_VIEWBOX_HEIGHT = 36;
const GRAPH_LEFT = 6;
const GRAPH_RIGHT = 94;

const getCallDate = getLatestInboundCallDate;

const formatDuration = (seconds) => {
  const totalSeconds = Math.max(0, Math.round(Number(seconds) || 0));

  if (!totalSeconds) return "0m";

  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;

  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const minutesRemainder = minutes % 60;
    return `${hours}h ${minutesRemainder}m`;
  }

  if (remainingSeconds === 0) {
    return `${minutes}m`;
  }

  return `${minutes}m ${remainingSeconds}s`;
};

const formatCount = (value) => Number(value || 0).toLocaleString("en-US");

const mapGraphValueToY = (value, axisMax) => {
  const normalizedValue = Math.max(GRAPH_MIN, Math.min(axisMax, Number(value) || 0));
  const graphRange = axisMax - GRAPH_MIN;

  if (graphRange === 0) {
    return GRAPH_BOTTOM;
  }

  return (
    GRAPH_BOTTOM -
    ((normalizedValue - GRAPH_MIN) / graphRange) * (GRAPH_BOTTOM - GRAPH_TOP)
  );
};

// niceStep: snaps a raw per-gridline step up to a round number (1/2/5/10/20/25/50... times a
// power of 10) so the y-axis reads like 10/20/30 instead of an arbitrary 7/14/21. Never below 1 -
// call counts are always whole numbers, so a "1.5 calls" gridline would never make sense.
const niceStep = (rawStep) => {
  if (rawStep <= 1) return 1;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalized = rawStep / magnitude;
  let niceNormalized;
  if (normalized <= 1) niceNormalized = 1;
  else if (normalized <= 2) niceNormalized = 2;
  else if (normalized <= 5) niceNormalized = 5;
  else niceNormalized = 10;
  return niceNormalized * magnitude;
};

// Scales the axis to the actual data instead of a fixed 0-4, rounding the top of the axis up to
// the next round-number step (see niceStep) so gridlines land on whole, round numbers.
const buildAxisScale = (values) => {
  const highestValue = Math.max(1, ...values.map((value) => Number(value) || 0));
  const axisStep = niceStep(highestValue / 4);
  const axisMax = axisStep * 4;
  const axisLabels = Array.from({ length: 5 }, (_, index) => axisMax - index * axisStep);
  return { axisMax, axisLabels };
};

// Same idea as buildAxisScale, but the step is always a multiple of 5 (5/10/15/20...) - used for
// the multi-day view specifically, per explicit request, rather than the general "nice round
// number" rule above.
const buildAxisScaleMultipleOfFive = (values) => {
  const highestValue = Math.max(1, ...values.map((value) => Number(value) || 0));
  const axisStep = Math.max(5, Math.ceil(highestValue / 4 / 5) * 5);
  const axisMax = axisStep * 4;
  const axisLabels = Array.from({ length: 5 }, (_, index) => axisMax - index * axisStep);
  return { axisMax, axisLabels };
};

const buildPointCoords = (values, axisMax) => {
  if (!values.length) return [];

  return values.map((value, index) => {
    const x =
      values.length === 1
        ? (GRAPH_LEFT + GRAPH_RIGHT) / 2
        : GRAPH_LEFT + (index / (values.length - 1)) * (GRAPH_RIGHT - GRAPH_LEFT);
    const y = mapGraphValueToY(value, axisMax);
    return [x, y];
  });
};

const buildLinePath = (points) => {
  if (!points.length) return "";

  if (points.length === 1) {
    const [x, y] = points[0];
    return `M ${x} ${y} L ${x + 0.1} ${y}`;
  }

  return points.reduce((path, point, index) => {
    if (index === 0) {
      return `M ${point[0]} ${point[1]}`;
    }

    const previous = points[index - 1];
    const midpointX = (previous[0] + point[0]) / 2;
    return `${path} C ${midpointX} ${previous[1]}, ${midpointX} ${point[1]}, ${point[0]} ${point[1]}`;
  }, "");
};

const buildAreaPath = (points) => {
  const linePath = buildLinePath(points);
  if (!linePath) return "";

  const firstPoint = points[0];
  const lastPoint = points[points.length - 1];

  return `${linePath} L ${lastPoint[0]} ${GRAPH_BOTTOM} L ${firstPoint[0]} ${GRAPH_BOTTOM} Z`;
};

// formatDateKeyLabel: "YYYY-MM-DD" -> "Jul 23", for the graph header - kept independent of
// xAxisLabels since those switch to time-of-day strings for a single-day range (below).
const formatDateKeyLabel = (dateKey) => {
  const date = safeDate(`${dateKey}T00:00:00`);
  if (!date) return dateKey;
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(date);
};

// findFirstHourBoundaryAtOrAfter: epoch ms of the next whole clock-hour, in `timeZone`, at or
// after `epochMs` (e.g. 1:17 PM -> 2:00 PM). Anchors buildHourTicks below so every tick lands on
// a real hour instead of an arbitrary fraction of the selected range.
const findFirstHourBoundaryAtOrAfter = (epochMs, timeZone) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(epochMs));
  const minute = Number(parts.find((part) => part.type === "minute")?.value || 0);
  const second = Number(parts.find((part) => part.type === "second")?.value || 0);
  const msIntoHour = minute * 60000 + second * 1000;
  return msIntoHour === 0 ? epochMs : epochMs + (3600000 - msIntoHour);
};

// buildHourTicks: real clock-hour marks (12 AM, 1 AM, ... in the practice's own timezone),
// positioned by their true elapsed-time proportion within [rangeStart, rangeEnd]. Both the label
// and the x-position come from the same timezone-aware instant, so they can never drift apart
// the way a browser-local relabeling of an already timezone-correct bucket range previously did
// (buckets were always correct server-side - only these labels were silently browser-local).
const buildHourTicks = (rangeStart, rangeEnd, timeZone) => {
  const startMs = rangeStart.getTime();
  const endMs = rangeEnd.getTime();
  const totalMs = endMs - startMs;
  if (!(totalMs > 0)) return [];

  const labelFormatter = new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric" });
  const ticks = [];
  let cursor = findFirstHourBoundaryAtOrAfter(startMs, timeZone);
  let guard = 0;
  while (cursor <= endMs && guard < 200) {
    const left = ((cursor - startMs) / totalMs) * 100;
    ticks.push({
      label: labelFormatter.format(new Date(cursor)),
      left: `${left}%`,
      align: left < 10 ? "start" : left > 90 ? "end" : "center",
    });
    cursor += 3600000;
    guard += 1;
  }
  return ticks;
};

// bucketTimeFormatter: precise "1:30 PM" formatter (in `timeZone`) for one bucket's start time -
// used by the hover tooltip so it shows the real time of day a call happened, not a relative
// countdown like "30m ago".
const bucketTimeFormatter = (timeZone) =>
  new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", minute: "2-digit" });

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
// Beyond this many days, group into weekly buckets instead of one bucket per day - a 20-day
// range should still show 20 individual daily points (per explicit request), so this only kicks
// in for a genuinely long range where one point per day would no longer be readable. No longer
// kept equal to Dashboard.jsx's own TREND_WEEKLY_BUCKET_THRESHOLD_DAYS (still 14 there) - that
// chart wasn't asked to change.
const MAX_DAILY_BUCKETS = 31;

// getDayCount: inclusive count of calendar days spanned by two "YYYY-MM-DD" date keys.
const getDayCount = (startDate, endDate) => {
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  return Math.max(1, Math.round((end.getTime() - start.getTime()) / ONE_DAY_MS) + 1);
};

// computeRangeBucketing: how many buckets to request from the backend for a given date range,
// and how to label them. A single day keeps the existing 4-bucket time-of-day view. A short
// multi-day range gets one bucket per calendar day, so every point on the graph gets its own
// distinct label instead of 5 points interpolated across the range landing on repeated days
// (e.g. a 3-day range rendering "Jul 21, Jul 21, Jul 22, Jul 23, Jul 23"). A longer range switches
// to weekly buckets, same threshold Dashboard.jsx already uses for its own trend chart.
const computeRangeBucketing = (startDate, endDate) => {
  if (!startDate || !endDate || startDate === endDate) {
    return { bucketCount: ROLLING_WINDOW_BUCKETS, isSingleDay: true };
  }
  const dayCount = getDayCount(startDate, endDate);
  return dayCount <= MAX_DAILY_BUCKETS
    ? { bucketCount: dayCount, isSingleDay: false, isDaily: true }
    : { bucketCount: Math.ceil(dayCount / 7), isSingleDay: false, isDaily: false };
};

// formatWeekRangeLabel: compact "Jul 5-11" / "Jul 28-Aug 3" label for a bucket spanning multiple
// days, mirroring Dashboard.jsx's identical helper.
const formatWeekRangeLabel = (bucketStart, bucketEnd) => {
  const monthFormatter = new Intl.DateTimeFormat("en-US", { month: "short" });
  const dayFormatter = new Intl.DateTimeFormat("en-US", { day: "numeric" });
  const startMonth = monthFormatter.format(bucketStart);
  const endMonth = monthFormatter.format(bucketEnd);
  if (startMonth === endMonth) {
    return `${startMonth} ${dayFormatter.format(bucketStart)}-${dayFormatter.format(bucketEnd)}`;
  }
  return `${startMonth} ${dayFormatter.format(bucketStart)}-${endMonth} ${dayFormatter.format(bucketEnd)}`;
};

// buildMultiDayXAxisLabels: one label per bucket for a multi-day range - a real calendar day
// ("Jul 21") when there are few enough days to give each its own bucket, or a compact week range
// ("Jul 21-27") once the range is long enough that the backend grouped multiple days per bucket.
const buildMultiDayXAxisLabels = (startTime, endTime, bucketCount, isDaily) => {
  if (isDaily) {
    // Whole calendar-day arithmetic, not fractional interpolation between startTime/endTime -
    // endTime sits at 23:59:59.999 on the last day (a few ms short of a clean N-day span), so
    // dividing that span by bucketCount and walking forward drifted each bucket's start earlier
    // by a growing fraction of a day, eventually landing a whole day behind (e.g. a 3-day range
    // rendering "Jul 21, Jul 21, Jul 22" instead of "Jul 21, Jul 22, Jul 23"). Stepping by exact
    // whole days from startTime's own calendar date sidesteps that entirely.
    const dayStart = new Date(startTime.getFullYear(), startTime.getMonth(), startTime.getDate());
    return Array.from({ length: bucketCount }, (_, index) => {
      const bucketDate = new Date(dayStart.getTime() + index * ONE_DAY_MS);
      return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(bucketDate);
    });
  }

  const totalMs = endTime.getTime() - startTime.getTime();
  const bucketWidthMs = totalMs / bucketCount;

  return Array.from({ length: bucketCount }, (_, index) => {
    const bucketStart = new Date(startTime.getTime() + index * bucketWidthMs);
    const bucketEnd = new Date(bucketStart.getTime() + bucketWidthMs - 1);
    return formatWeekRangeLabel(bucketStart, bucketEnd);
  });
};

const InboundOverview = ({
  practiceContactNumber,
  inboundContact,
  inBoundsCalls = [],
  startDate,
  endDate,
}) => {
  const { PracticeId, token, authorizedGetRequest, timezonePreferenceVersion } = useContextStateManagement();
  const [timeRangeKey, setTimeRangeKey] = useState("today");
  const [activePointIndex, setActivePointIndex] = useState(null);
  // Once a real date range is selected elsewhere on the page, the graph switches from a
  // rolling "last N hours from right now" window to that range - mirrors OutboundOverview.jsx's
  // identical hasDateRange switch.
  const hasDateRange = Boolean(startDate) && Boolean(endDate);

  // Now covers BOTH a real calendar date range AND the rolling 2h/6h/12h/24h window (via the
  // `hours` param) - previously only the date-range case called this endpoint, and the rolling
  // window instead counted inBoundsCalls client-side below, which only ever covers calls already
  // matched to an uploaded lead by phone number - a call from a caller who isn't (yet) a saved
  // lead never showed up here at all, at any hour, even from today.
  const [apiVolume, setApiVolume] = useState(null);
  const selectedRollingRange =
    TIME_RANGES.find((range) => range.key === timeRangeKey) || TIME_RANGES[TIME_RANGES.length - 1];

  useEffect(() => {
    if (!PracticeId || !token) {
      setApiVolume(null);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const params = hasDateRange
          ? {
              PracticeId,
              startDate,
              endDate,
              buckets: computeRangeBucketing(startDate, endDate).bucketCount,
            }
          : selectedRollingRange.isToday
            ? { PracticeId, today: true, buckets: ROLLING_WINDOW_BUCKETS }
            : { PracticeId, hours: selectedRollingRange.hours, buckets: ROLLING_WINDOW_BUCKETS };
        const data = await authorizedGetRequest(token, "/api/getInboundCallVolume", params);
        if (!cancelled) {
          setApiVolume(data);
        }
      } catch (error) {
        if (!cancelled) {
          setApiVolume(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // authorizedGetRequest intentionally excluded - ContextApi.js doesn't memoize its functions,
    // so including it would re-fetch on every unrelated context re-render instead of only when
    // the date range/rolling window actually changes (same rationale as OutboundOverview.jsx's
    // identical effect).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDateRange, PracticeId, token, startDate, endDate, selectedRollingRange.hours, selectedRollingRange.isToday, timezonePreferenceVersion]);

  // Every lead can carry multiple real call attempts in row.Calls[] (sourced live from
  // TwilioLogs) - counting one entry per lead undercounts total volume. Flatten to one
  // entry per attempt instead; leads with no attempt breakdown fall back to a single entry
  // so they aren't dropped. Shared by both the all-time KPI card and the windowed graph.
  const flattenAttempts = (leadRows) =>
    (Array.isArray(leadRows) ? leadRows : [])
      .flatMap((leadRow) => {
        const calls = Array.isArray(leadRow?.Calls) ? leadRow.Calls : [];
        if (calls.length === 0) {
          return [{ row: leadRow, date: getCallDate(leadRow) }];
        }
        return calls.map((call) => ({
          row: call,
          date: safeDate(call?.startedAt || call?.CreateDateTime || call?.createDateTime),
        }));
      })
      .filter((item) => item.date)
      .sort((left, right) => left.date - right.date);

  // The hero card's KPI numbers (Total/Answered/Missed/Avg Duration) scope to the same calendar
  // date range as the "Calls by Time" graph below it, when one is selected - matching
  // OutboundOverview.jsx's identical apiKpiTotals. With no range picked, they show true all-time
  // totals, same as before. This reads from the same widened-number/widened-account pipeline
  // Dashboard uses (buildPracticeConversations), instead of counting inBoundsCalls (only
  // whatever leads this page has loaded, and only calls that could be matched to one of THOSE
  // leads by phone number) - that undercounts on any practice with real calls from a caller who
  // has no saved Lead record at all. Deliberately NOT scoped to the graph's own rolling
  // 2h/6h/Today picker (selectedRollingRange) - that's a graph-only zoom control, not a
  // page-level filter, so the hero cards ignore it the same way they did before.
  const [apiKpiTotals, setApiKpiTotals] = useState(null);

  useEffect(() => {
    if (!PracticeId || !token) {
      setApiKpiTotals(null);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const params = hasDateRange ? { PracticeId, startDate, endDate } : { PracticeId };
        const data = await authorizedGetRequest(token, "/api/getInboundCallVolume", params);
        if (!cancelled) {
          setApiKpiTotals(data);
        }
      } catch (error) {
        if (!cancelled) {
          setApiKpiTotals(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PracticeId, token, hasDateRange, startDate, endDate, timezonePreferenceVersion]);

  const kpiStats = useMemo(
    () => ({
      totalCalls: apiKpiTotals?.totalCalls || 0,
      answeredCalls: apiKpiTotals?.answeredCalls || 0,
      missedCalls: apiKpiTotals?.missedCalls || 0,
      averageDuration: formatDuration(apiKpiTotals?.averageDurationSec || 0),
    }),
    [apiKpiTotals],
  );

  const overview = useMemo(() => {
    // No demo-looking fallback number - if the practice's own inbound number genuinely isn't
    // configured, say so plainly instead of silently showing a fake-looking one.
    const agentNumber = inboundContact || practiceContactNumber || "Not available";

    if (apiVolume) {
      const { bucketCount, isSingleDay, isDaily } = hasDateRange
        ? computeRangeBucketing(startDate, endDate)
        : { bucketCount: ROLLING_WINDOW_BUCKETS, isSingleDay: true, isDaily: false };
      const bucketCounts =
        Array.isArray(apiVolume.bucketCounts) && apiVolume.bucketCounts.length
          ? apiVolume.bucketCounts
          : new Array(bucketCount).fill(0);
      const graphValues = bucketCounts;

      // Multi-day y-axis is always in steps of 5 (per explicit request); the intraday/rolling
      // view keeps the general "nice round number" rule instead.
      const { axisMax, axisLabels: yAxisLabels } = isSingleDay
        ? buildAxisScale(graphValues)
        : buildAxisScaleMultipleOfFive(graphValues);
      const pointCoords = buildPointCoords(graphValues, axisMax);

      let axisTicks;
      let xAxisLabels;

      if (isSingleDay) {
        // The exact instants the backend actually bucketed by (whether from the rolling
        // Past 2h/6h/Today picker or a single calendar day) - the labels below are formatted in
        // the viewer's own effective timezone (their PreferredTimezone, resolved fresh by the
        // backend on every request), which updates immediately if they change it via the
        // Settings timezone selector, and otherwise stays in sync with their computer's own
        // clock automatically.
        const rangeStartMs = apiVolume.rangeStart ? new Date(apiVolume.rangeStart).getTime() : null;
        const rangeEndMs = apiVolume.rangeEnd ? new Date(apiVolume.rangeEnd).getTime() : null;
        const timeZone = apiVolume.practiceTimezone || DEFAULT_PRACTICE_TIMEZONE;

        if (rangeStartMs !== null && rangeEndMs !== null && rangeEndMs > rangeStartMs) {
          const bucketWidthMs = (rangeEndMs - rangeStartMs) / bucketCounts.length;
          const formatter = bucketTimeFormatter(timeZone);
          xAxisLabels = bucketCounts.map((_, index) =>
            formatter.format(new Date(rangeStartMs + index * bucketWidthMs)),
          );

          if (!hasDateRange && selectedRollingRange.isToday) {
            // "Today" can span anywhere from a few minutes to nearly 24 hours depending on what
            // time it is right now, so it could need just as many hour ticks as a multi-day range
            // - same fix as there: one combined "Data from 12 AM to 3 PM" label instead of one
            // per hour, using the exact instants the backend bucketed by (formatted in the
            // viewer's own timezone, same as everywhere else on this graph).
            const hourOnlyFormatter = new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric" });
            axisTicks = [
              {
                label: `Data from ${hourOnlyFormatter.format(new Date(rangeStartMs))} to ${hourOnlyFormatter.format(new Date(rangeEndMs))}`,
                left: "50%",
                align: "center",
              },
            ];
          } else {
            axisTicks = buildHourTicks(new Date(rangeStartMs), new Date(rangeEndMs), timeZone);
          }
        } else {
          axisTicks = [];
          xAxisLabels = bucketCounts.map(() => "");
        }
      } else {
        // Multi-day: whole calendar dates, not clock times - immune to the timezone-label bug
        // above (a calendar day is the same day regardless of which timezone displays it).
        // Per-day/week labels (e.g. "Aug 5") still feed the hover tooltip below, but the visible
        // axis shows just one combined "Data for Aug 1-15" mark instead of one label per point -
        // one label per day was what kept forcing the graph wider than the space it's given.
        const startTime = new Date(`${startDate}T00:00:00`);
        const endTime = new Date(`${endDate}T23:59:59.999`);
        const multiDayLabels = buildMultiDayXAxisLabels(startTime, endTime, bucketCount, isDaily);
        xAxisLabels = multiDayLabels;
        axisTicks = [
          { label: `Data for ${formatWeekRangeLabel(startTime, endTime)}`, left: "50%", align: "center" },
        ];
      }

      return {
        agentNumber,
        pointCoords,
        linePath: buildLinePath(pointCoords),
        areaPath: buildAreaPath(pointCoords),
        xAxisLabels,
        axisTicks,
        yAxisLabels,
        axisMax,
        graphValues,
      };
    }

    // Fallback for when the API call itself failed - counts from whatever inBoundsCalls the page
    // already has loaded (only leads already matched by phone number, so this undercounts vs the
    // API path above, same caveat as before this rework). No server data is available here at
    // all, so there's no PreferredTimezone to read - this is the one spot that genuinely has
    // nothing better than the browser's own clock to fall back on.
    const timeZone = FALLBACK_TIME_ZONE;
    const now = new Date();
    const startTime = selectedRollingRange.isToday
      ? new Date(now.getFullYear(), now.getMonth(), now.getDate())
      : new Date(now.getTime() - selectedRollingRange.hours * 60 * 60 * 1000);

    const rows = flattenAttempts(inBoundsCalls).filter(
      (item) => item.date >= startTime && item.date <= now,
    );

    const bucketWidthMs = (now.getTime() - startTime.getTime()) / ROLLING_WINDOW_BUCKETS;
    const bucketCounts = Array.from({ length: ROLLING_WINDOW_BUCKETS }, (_, index) => {
      const bucketStart = new Date(startTime.getTime() + index * bucketWidthMs);
      const bucketEnd = new Date(bucketStart.getTime() + bucketWidthMs);
      return rows.filter((item) => item.date >= bucketStart && item.date < bucketEnd).length;
    });
    const graphValues = bucketCounts;

    const { axisMax, axisLabels: yAxisLabels } = buildAxisScale(graphValues);
    const pointCoords = buildPointCoords(graphValues, axisMax);
    const formatter = bucketTimeFormatter(timeZone);
    const xAxisLabels = bucketCounts.map((_, index) =>
      formatter.format(new Date(startTime.getTime() + index * bucketWidthMs)),
    );
    const axisTicks = selectedRollingRange.isToday
      ? [
          {
            label: `Data from ${new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric" }).format(startTime)} to ${new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric" }).format(now)}`,
            left: "50%",
            align: "center",
          },
        ]
      : buildHourTicks(startTime, now, timeZone);

    return {
      agentNumber,
      pointCoords,
      linePath: buildLinePath(pointCoords),
      areaPath: buildAreaPath(pointCoords),
      xAxisLabels,
      axisTicks,
      yAxisLabels,
      axisMax,
      graphValues,
    };
  }, [
    inBoundsCalls,
    inboundContact,
    practiceContactNumber,
    hasDateRange,
    startDate,
    endDate,
    apiVolume,
    selectedRollingRange.hours,
    selectedRollingRange.isToday,
  ]);

  const bottomMetrics = [
    {
      label: "Answered",
      value: kpiStats.answeredCalls,
      tone: "answered",
      info: "Inbound calls that were answered.",
    },
    {
      label: "Missed",
      value: kpiStats.missedCalls,
      tone: "missed",
      info: "Inbound calls that went unanswered.",
    },
    {
      label: "Avg Duration",
      value: kpiStats.averageDuration,
      tone: "duration",
      info: "Average length of answered inbound calls.",
    },
  ];

  const activePoint = overview.pointCoords[activePointIndex] || null;
  const activePointLabel = overview.xAxisLabels[activePointIndex] || "";
  const activePointValue = overview.graphValues[activePointIndex] ?? 0;
  const activePointLeft = activePoint ? `${activePoint[0]}%` : "0%";
  const activePointTop = activePoint
    ? `${(activePoint[1] / GRAPH_VIEWBOX_HEIGHT) * 100}%`
    : "0%";
  const tooltipSide =
    activePointIndex !== null && activePointIndex >= overview.pointCoords.length - 2
      ? "right"
      : "left";
  const gridLineYs = overview.yAxisLabels.map((label) => mapGraphValueToY(label, overview.axisMax));
  const yAxisTicks = overview.yAxisLabels.map((label) => ({
    label,
    top: `${(mapGraphValueToY(label, overview.axisMax) / GRAPH_VIEWBOX_HEIGHT) * 100}%`,
  }));
  // Plain hour marks (1 PM, 2 PM...), positioned by real elapsed time - decoupled from how many
  // data buckets exist (there are far more of those than would be readable as labels).
  const xAxisTicks = overview.axisTicks;

  return (
    <section className="inbound-overview">
      <article
        className="inbound-overview__card inbound-overview__card--agent"
      >
        <div className="inbound-overview__agent-top">
          <div className="inbound-overview__agent-head">
            <div className="inbound-overview__agent-icon">
              <PhoneIncoming size={18} strokeWidth={2.2} />
            </div>
            <div className="inbound-overview__agent-copy">
              <div className="inbound-overview__agent-name">Inbound Agent</div>
              <div className="inbound-overview__agent-number">
                {overview.agentNumber}
              </div>
            </div>
          </div>
          <div className="inbound-overview__agent-total">
            <div className="inbound-overview__agent-total-label">
              Total Calls
              <InfoTooltip text="All inbound calls received." />
            </div>
            <div className="inbound-overview__agent-total-value">
              {formatCount(kpiStats.totalCalls)}
            </div>
          </div>
        </div>

        <div className="inbound-overview__agent-divider" />

        <div className="inbound-overview__metrics-row inbound-overview__metrics-row--agent">
          {bottomMetrics.map((metric) => (
            <article className="inbound-metric" key={metric.label}>
              <div className="inbound-metric__label">
                {metric.label}
                <InfoTooltip text={metric.info} />
              </div>
              <div
                className={`inbound-metric__value inbound-metric__value--${metric.tone}`}
              >
                {typeof metric.value === "number"
                  ? formatCount(metric.value)
                  : metric.value}
              </div>
            </article>
          ))}
        </div>
      </article>

      <article className="inbound-overview__card inbound-overview__card--graph">
        <div className="inbound-overview__graph-head">
          <div className="inbound-overview__graph-topline">
            <div className="inbound-overview__graph-title-group">
              <h3>
                Calls by Time
                <InfoTooltip text="Inbound call volume over the selected time range." />
              </h3>
            </div>
            <div className="inbound-overview__graph-controls">
              {hasDateRange ? (
                <span className="inbound-overview__filters-static">
                  {startDate === endDate
                    ? formatDateKeyLabel(startDate)
                    : `${formatDateKeyLabel(startDate)} – ${formatDateKeyLabel(endDate)}`}
                </span>
              ) : (
                <div
                  className="inbound-overview__filters"
                  role="tablist"
                  aria-label="Inbound time filters"
                >
                  {TIME_RANGES.map((range) => (
                    <button
                      key={range.key}
                      type="button"
                      className={`inbound-overview__filter${
                        timeRangeKey === range.key ? " active" : ""
                      }`}
                      onClick={() => setTimeRangeKey(range.key)}
                      aria-label={`Show ${range.isToday ? "today's" : `the past ${range.hours} hour`} range`}
                    >
                      {range.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="inbound-overview__graph-shell">
          <div className="inbound-overview__y-axis">
            <div className="inbound-overview__y-axis-track">
              {yAxisTicks.map((tick, index) => (
                <span
                  key={`${tick.label}-${index}`}
                  className="inbound-overview__y-axis-tick"
                  style={{ top: tick.top }}
                >
                  {tick.label}
                </span>
              ))}
            </div>
          </div>

          <div className="inbound-overview__graph-content">
            <div
              className="inbound-overview__graph-scroll"
              style={{
                // Always exactly the space this card is given, never wider - no matter how many
                // points or ticks a range produces, the graph packs into the fixed width instead
                // of growing the card sideways (multi-day used to grow one dedicated slot per
                // day/week label; the single-day/rolling view used to grow with its hour-tick
                // count between Past 2h/6h/Today).
                width: "100%",
              }}
            >
              <div
                className="inbound-overview__graph-stage"
                onMouseLeave={() => setActivePointIndex(null)}
              >
                {activePoint && (
                  <>
                    <div
                      className="inbound-overview__guide-line"
                      style={{ left: activePointLeft }}
                    />
                    <div
                      className={`inbound-overview__tooltip inbound-overview__tooltip--${tooltipSide}`}
                      style={{ left: activePointLeft, top: activePointTop }}
                    >
                      <div className="inbound-overview__tooltip-time">
                        {activePointLabel}
                      </div>
                      <div className="inbound-overview__tooltip-value">
                        calls : {activePointValue}
                      </div>
                    </div>
                  </>
                )}

                <div className="inbound-overview__graph">
                <svg
                  viewBox={`0 0 100 ${GRAPH_VIEWBOX_HEIGHT}`}
                  preserveAspectRatio="none"
                  aria-hidden="true"
                >
                  <defs>
                    <linearGradient id="inboundGraphFill" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#63C9DC" stopOpacity="0.25" />
                      <stop offset="100%" stopColor="#63C9DC" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  {gridLineYs.map((y, index) => (
                    <path
                      key={`grid-${y}-${index}`}
                      className="inbound-overview__gridline"
                      d={`M 0 ${y} H 100`}
                    />
                  ))}
                  <path className="inbound-overview__area" d={overview.areaPath} />
                  <path className="inbound-overview__line" d={overview.linePath} />
                  {overview.pointCoords.map(([x, y], index) => (
                    <g
                      key={`${x}-${y}-${index}`}
                      className="inbound-overview__point-group"
                      onMouseEnter={() => setActivePointIndex(index)}
                    >
                      {index === activePointIndex && (
                        <circle
                          className="inbound-overview__point-glow"
                          cx={x}
                          cy={y}
                          r="1.45"
                        />
                      )}
                      <circle
                        className={`inbound-overview__point${
                          index === activePointIndex
                            ? " inbound-overview__point--active"
                            : ""
                        }`}
                        cx={x}
                        cy={y}
                        r={index === activePointIndex ? "0.76" : "0.62"}
                      />
                    </g>
                  ))}
                </svg>
                </div>
              </div>

              <div className="inbound-overview__x-axis">
                <div className="inbound-overview__x-axis-track">
                  {xAxisTicks.map((tick, index) => (
                    <span
                      key={`${tick.label}-${index}`}
                      className={`inbound-overview__x-axis-tick inbound-overview__x-axis-tick--${tick.align}`}
                      style={{ left: tick.left }}
                    >
                      {tick.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>
    </section>
  );
};

export default InboundOverview;
